/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomTextFieldWithChips.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import { Box, Chip, Stack } from "@mui/material";
import CustomFormTextFieldV2 from "./CustomFormTextFieldV2";
import CustomButtonV2 from "../button/CustomButtonV2";
import AddIcon from "@mui/icons-material/Add";

const chipStyle = {
  backgroundColor: "#53565A",
  color: "white",
  borderRadius: 0,
  "& .MuiChip-deleteIcon": {
    color: "white",
    fontSize: 18,
    "&:hover": {
      color: "red",
    },
  },
};

const CustomTextFieldWithChips = ({
  label = "Enter value",
  placeholder = "Type something...",
  onChangeChips,
  value,
  setValue,
  required,
}) => {
  const [chips, setChips] = useState([]);

  const handleAdd = () => {
    if (!value?.trim()) return;

    const newChips = [...chips, value.trim()];
    setChips(newChips);
    setValue("");

    if (onChangeChips) onChangeChips(newChips);
  };

  const handleDelete = (chipToDelete) => {
    const updatedChips = chips?.filter((chip) => chip !== chipToDelete);
    setChips(updatedChips);

    if (onChangeChips) onChangeChips(updatedChips);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Row: Input + Add Button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomFormTextFieldV2
          label={label}
          required={required}
          value={value}
          setValue={setValue}
          placeholder={placeholder}
        />

        <CustomButtonV2
          startIcon={<AddIcon />}
          type="button"
          handleOnClick={handleAdd}
        />
      </Box>

      {/* Chips Below */}
      <Stack
        direction="row"
        spacing={1.5}
        mt={2}
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          pb: 1,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bfbfbf",
            borderRadius: 10,
          },
        }}
      >
        {chips?.map((chip, index) => (
          <Chip
            key={index}
            label={chip}
            onDelete={() => handleDelete(chip)}
            sx={{
              ...chipStyle,
              px: 1.2,
              py: 0.5,
              mr: 1.2,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default CustomTextFieldWithChips;
